import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { PublicLayout } from '@/components/public/PublicLayout';
import { AppLayout } from '@/components/common/AppLayout';
import { AuthGuard } from '@/components/common/AuthGuard';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { RouteError } from '@/components/common/RouteError';

// Public portal (respondents, no login)
const Home = lazy(() => import('@/pages/public/Home'));
const RequestWizard = lazy(() => import('@/pages/public/RequestWizard'));
const RequestSuccess = lazy(() => import('@/pages/public/RequestSuccess'));
const Track = lazy(() => import('@/pages/public/Track'));
const Privacy = lazy(() => import('@/pages/public/Privacy'));
// Staff
const Login = lazy(() => import('@/pages/auth/Login'));
const SsoCallback = lazy(() => import('@/pages/auth/SsoCallback'));
const Dashboard = lazy(() => import('@/pages/admin/Dashboard'));
const Requests = lazy(() => import('@/pages/admin/Requests'));
const RequestDetail = lazy(() => import('@/pages/admin/RequestDetail'));
const InternalRequest = lazy(() => import('@/pages/admin/InternalRequest'));
const Events = lazy(() => import('@/pages/admin/Events'));
const EventForm = lazy(() => import('@/pages/admin/EventForm'));
const Users = lazy(() => import('@/pages/admin/Users'));

const wrap = (element: React.ReactNode) => <Suspense fallback={<LoadingSpinner fullScreen />}>{element}</Suspense>;

export const router = createBrowserRouter([
  // ── Public portal ─────────────────────────────────────────────────────────
  {
    path: '/',
    element: <PublicLayout />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: wrap(<Home />) },
      { path: 'nueva', element: wrap(<RequestWizard />) },
      { path: 'gracias/:code', element: wrap(<RequestSuccess />) },
      { path: 'seguimiento', element: wrap(<Track />) },
      { path: 'seguimiento/:code', element: wrap(<Track />) },
      { path: 'privacidad', element: wrap(<Privacy />) },
      // Partner-staff forms, no login (identified by e-mail, throttled server-side)
      { path: 'registro-evento', element: wrap(<EventForm mode="public" />) },
      { path: 'registro-evento/:id', element: wrap(<EventForm mode="public" />) },
      { path: 'registro-interno', element: wrap(<InternalRequest mode="public" />) },
    ],
  },

  // ── Auth ──────────────────────────────────────────────────────────────────
  { path: '/login', element: wrap(<Login />), errorElement: <RouteError /> },
  { path: '/sso-callback', element: wrap(<SsoCallback />), errorElement: <RouteError /> },

  // ── Staff back-office (JWT-protected) ─────────────────────────────────────
  {
    path: '/admin',
    element: (
      <AuthGuard>
        <AppLayout />
      </AuthGuard>
    ),
    errorElement: <RouteError />,
    children: [
      { index: true, element: <Navigate to="dashboard" replace /> },
      { path: 'dashboard', element: wrap(<Dashboard />) },
      { path: 'solicitudes', element: wrap(<Requests />) },
      { path: 'solicitudes/nueva', element: wrap(<InternalRequest />) },
      { path: 'solicitudes/:id', element: wrap(<RequestDetail />) },
      { path: 'eventos', element: wrap(<Events />) },
      { path: 'eventos/nuevo', element: wrap(<EventForm />) },
      { path: 'eventos/:id', element: wrap(<EventForm />) },
      { path: 'usuarios', element: <AuthGuard roles={['ADMIN']}>{wrap(<Users />)}</AuthGuard> },
    ],
  },

  { path: '*', element: <Navigate to="/" replace /> },
]);
